import React, { useState } from "react";
import { Input } from "@headlessui/react";
import { cn } from "../../utils/cn";
import { AlertCircle } from "lucide-react";

interface InputFieldProps {
  label?: string;
  id?: string;
  value: string;
  onChange: (value: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  error?: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  icon?: React.ReactNode;
  name?: string;
  loading?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const FleetrifyInputField: React.FC<InputFieldProps> = ({
  label,
  id,
  value,
  name,
  type,
  onChange,
  placeholder,
  className,
  labelClassName,
  inputClassName,
  error,
  disabled,
  required,
  icon,
  loading = false,
  onPaste,
  onKeyUp,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || !!value;

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-[13px] font-medium transition-colors duration-200",
            error ? "text-red-500" : isActive ? "text-[#143887]" : "text-black",
            labelClassName
          )}
        >
          {label} {required && <span className="text-[#EB5757]">*</span>}
        </label>
      )}

      <div className="relative grid place-items-center">
        <Input
          id={id}
          type={type || "text"}
          value={value}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyUp={onKeyUp}
          onPaste={onPaste}
          className={cn(
            "w-full px-4 py-3 rounded-md placeholder:text-xs text-sm border transition-colors duration-200",
            isActive ? "border-[#143887]" : "border-gray-300",
            error && "border-red-500 focus:ring-red-500",
            "focus:outline-none focus:ring-[0.5] focus:ring-[#143887]",
            inputClassName,
            (disabled || loading) &&
            "text-gray-600 bg-[#F5F6FA] cursor-not-allowed"
          )}
          aria-invalid={!!error}
          disabled={disabled || loading} // 🛑 Prevent typing if loading
        />
        {loading ? (
          <div className="absolute right-0 pr-4">
            <div className="h-4 w-4 border-2 border-gray-300 border-t-[#143887] rounded-full animate-spin"></div>
          </div>
        ) : (
          icon && <div className="absolute right-0 pr-4">{icon}</div>
        )}
      </div>

      {error && (
        <p className="text-red-600 font-medium text-xs flex items-center">
          <AlertCircle size={18} className="mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};
